import React from "react";
import { TFunction } from "i18next";
import {
  ArrowUpRight,
  ConvertDealToContract,
  ConvertLeadToDeal,
  type SvgIcon
} from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import {
  EntityType,
  entityTypeToEntityName,
  entityTypeToSingleEntityName
} from "@/services/hooks/entities/useEntityType";

/**
 * Describes a record whose activity area is closed because it was converted or
 * inactivated into a linked record. Record-type-agnostic: the noun, verb and
 * target are all resolved by the caller from the record's state, so the same
 * empty state renders for any conversion/disqualification flow.
 */
export interface ClosedRecordActivityState {
  /** The record whose activity area is closed (e.g. the deal). */
  sourceEntityType: EntityType;
  /** The record activity now lives on (e.g. the contract). */
  targetEntityType: EntityType;
  /**
   * Fully interpolated message, e.g.
   * "This Deal has been converted to a Contract. New activity is tracked on the linked record."
   */
  message: string;
  /** Label for the navigation action, e.g. "View contract activity". */
  viewLinkedRecordLabel: string;
  /** Navigates to the linked record where activity is now tracked. */
  onViewLinkedRecord: () => void;
}

/**
 * Builds the descriptor from a record's terminal state. `action` is the already-
 * localized verb phrase (e.g. "converted to a Deal", "disqualified"). Copy, label
 * and navigation are derived from the entity types so every case stays consistent.
 */
export const buildClosedRecordState = (params: {
  sourceEntityType: EntityType;
  targetEntityType: EntityType;
  targetId: string;
  action: string;
  navigate: (path: string) => void;
  t: TFunction;
}): ClosedRecordActivityState => {
  const { sourceEntityType, targetEntityType, targetId, action, navigate, t } =
    params;
  const targetName = t(
    `entities.names.${entityTypeToSingleEntityName[targetEntityType]}`
  );
  return {
    sourceEntityType,
    targetEntityType,
    message: t("activities.closedRecord.message", {
      record: t(
        `entities.names.${entityTypeToSingleEntityName[sourceEntityType]}`
      ),
      action
    }),
    viewLinkedRecordLabel: t("activities.closedRecord.viewLinkedRecord", {
      record: targetName.toLowerCase()
    }),
    onViewLinkedRecord: () =>
      navigate(`/${entityTypeToEntityName[targetEntityType]}/${targetId}`)
  };
};

// Combined source→target conversion illustrations (exported from Figma), one per
// supported conversion pair. Pairs without dedicated art (e.g. disqualified,
// closed lost) render text + link only, with no icon.
const getConversionIcon = (
  sourceEntityType: EntityType,
  targetEntityType: EntityType
): SvgIcon | undefined => {
  if (sourceEntityType === EntityType.LEAD && targetEntityType === EntityType.DEAL) {
    return ConvertLeadToDeal;
  }
  if (sourceEntityType === EntityType.DEAL && targetEntityType === EntityType.CONTRACT) {
    return ConvertDealToContract;
  }
  return undefined;
};

const ActivityClosedRecordEmptyState: React.FC<ClosedRecordActivityState> = ({
  sourceEntityType,
  targetEntityType,
  message,
  viewLinkedRecordLabel,
  onViewLinkedRecord
}) => {
  const ConversionIcon = getConversionIcon(sourceEntityType, targetEntityType);
  return (
    <div
      className="flex w-full flex-col items-center gap-3 py-6"
      data-test="activities-closed-record-empty-state"
    >
      {ConversionIcon && (
        <ConversionIcon
          aria-hidden="true"
          className="h-16 w-auto"
          data-test="activities-closed-record-icon"
        />
      )}
      <Text
        variant="bodySecondary"
        className="max-w-[40rem] text-center"
        data-test="activities-closed-record-message"
      >
        {message}
      </Text>
      <Button
        variant="secondary"
        onClick={onViewLinkedRecord}
        data-test="activities-closed-record-view-link"
      >
        <ArrowUpRight />
        {viewLinkedRecordLabel}
      </Button>
    </div>
  );
};

export default ActivityClosedRecordEmptyState;
